import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn
} from '@bliks-io/ui';
import type { FieldError } from '@/lib/excel';
import type { DraftRowShape, RowValidator } from '@/lib/draft-rows/draftRow';
import { clearStaleDuplicateMarks, type DuplicateCheck } from '@/lib/draft-rows/markDuplicateRows';

export interface DraftSelectOption {
  value: string;
  label: string;
  /** Rich rendering (e.g. PersonChip) shown in the dropdown and, via SelectValue, the trigger */
  display?: ReactNode;
}

export interface DraftColumnDef<TData> {
  field: keyof TData & string;
  /** i18n key for the header — reuse the Excel column keys so table and template match */
  headerKey: string;
  /** i18n key of a short header hint rendered under the label */
  descriptionKey?: string;
  /**
   * Renders a select storing string values when provided; otherwise a text
   * input. The function form resolves per row, e.g. the parent-department
   * dropdown excluding the row's own abbreviation.
   */
  options?: DraftSelectOption[] | ((row: DraftRowShape<TData>) => DraftSelectOption[]);
  /**
   * Fans a cell edit of this column out to sibling rows (abbreviation
   * re-pointing, shared Stelle description). Runs inside updateCell after the
   * edit is applied; must return a same-length array keeping identity (===)
   * entries for untouched rows — changed rows re-validate like the edited one.
   */
  syncRowsAfterEdit?: (
    rows: readonly TData[],
    editedIndex: number,
    previousValue: string | undefined
  ) => readonly TData[];
  /**
   * Renders the cell as plain text for matching rows even in an editable
   * table — e.g. the locked identity columns of protected member rows. The
   * function form resolves per row; `true` locks the whole column.
   */
  readOnly?: boolean | ((row: DraftRowShape<TData>) => boolean);
  /** Hover hint (i18n key) on cells this column's `readOnly` locked. */
  readOnlyHintKey?: string;
  /** Free-text input parsing: number maps '' to undefined and digits to a number */
  inputType?: 'text' | 'number';
  /** Optional fields get a clear entry in their select */
  optional?: boolean;
  /** Marks the header with a trailing asterisk */
  required?: boolean;
  widthClass?: string;
  /** Extra classes on the cell control, e.g. mono styling for email/URL columns */
  cellClassName?: string;
  /**
   * Shown instead of the raw value when the stored value matches no option
   * (e.g. an imported email that resolved to no user); also paints the
   * trigger red per the design's "Benutzer nicht gefunden" state.
   */
  unknownValueContent?: ReactNode;
  /**
   * Presence makes the column appear in the step's field explainer; the
   * label and required-ness come from `headerKey`/`required` so table,
   * template and explainer cannot drift.
   */
  explainer?: { icon: ReactNode; descriptionKey: string };
}

/**
 * Per-row delete affordance: 'disabled' keeps the button but renders it inert
 * (removal is blocked but should stay discoverable — e.g. a persisted
 * Funktion the wizard has no removal wire for), 'hidden' renders no button at
 * all (removal is unthinkable — e.g. protected members that only exist
 * server-side).
 */
export type RowDeleteAction = 'enabled' | 'disabled' | 'hidden';

/**
 * Excel column keys carry a baked-in "*" for the template sheet; on-screen
 * surfaces (table header, field explainer) render required-ness themselves,
 * so they strip it to avoid "Name* *".
 */
export const stripRequiredMarker = (label: string): string => label.replace(/\*$/, '');

const NONE_VALUE = '__none__';

const CELL_CONTROL_CLASS =
  'h-[34px] rounded-lg border border-line bg-white px-2.5 text-[13px] shadow-none';

type EditableDraftTableProps<TData, TRow extends DraftRowShape<TData>> = DraftTableBaseProps<
  TData,
  TRow
> &
  (
    | { onRowsChange?: never; validateRow?: never; duplicateChecks?: never }
    | {
        onRowsChange: (rows: TRow[]) => void;
        validateRow: RowValidator<TData>;
        /** Row identity, so an edit or delete clears duplicate marks that no longer hold */
        duplicateChecks: Array<DuplicateCheck<TData>>;
      }
  );

interface DraftTableBaseProps<TData, TRow extends DraftRowShape<TData>> {
  rows: TRow[];
  columns: DraftColumnDef<TData>[];
  /**
   * Plain-text rendering with no delete/add — used for the
   * already-existing entities so they look like the draft table.
   */
  readOnly?: boolean;
  /** Fresh draft appended by the manual-add row */
  makeEmptyRow?: (rowNumber: number) => TRow;
  /** i18n key of the add-row label, named after the entity ("Abteilung hinzufügen") */
  addLabelKey?: string;
  /** i18n key of the card header title; renders the header bar with the row-count pill */
  titleKey?: string;
  /** Actions rendered on the right of the card header (upload / clear buttons) */
  toolbar?: ReactNode;
  /** Per-row delete affordance (see RowDeleteAction). Defaults to 'enabled'. */
  deleteAction?: (row: TRow) => RowDeleteAction;
  /** Tooltip shown on a 'disabled' delete button. */
  deleteDisabledTitle?: string;
  /** Extra classes on a data row — marks rows with special provenance, e.g. Stelle-derived Funktionen. */
  rowClassName?: (row: TRow) => string | undefined;
}

function fieldFirstError(errors: FieldError[] | undefined, field: string): string | undefined {
  return errors?.find((error) => error.field === field)?.message;
}

function resolveColumnReadOnly<TData>(
  column: DraftColumnDef<TData>,
  row: DraftRowShape<TData>
): boolean {
  return typeof column.readOnly === 'function' ? column.readOnly(row) : (column.readOnly ?? false);
}

function readOnlyCellContent(
  rawValue: unknown,
  options: DraftSelectOption[] | undefined
): ReactNode {
  if (rawValue === undefined || rawValue === null || rawValue === '') return null;
  const value = String(rawValue as string | number);
  const optionLabel = options?.find((option) => option.value === value)?.label;
  return <span className="text-[13px] text-fg-secondary">{optionLabel ?? value}</span>;
}

/**
 * How a select cell's stored value relates to its options. Emails and names
 * resolve case-insensitively everywhere else (row validation, backend
 * imports) — a value differing only in case IS that option, so it must render
 * as the match and never as unknown.
 */
function matchSelectValue(value: string, options: DraftSelectOption[]) {
  const exactOption = options.some((option) => option.value === value);
  const caseInsensitiveOption = exactOption
    ? undefined
    : options.find((option) => option.value.toLowerCase() === value.toLowerCase());
  return {
    exactOption,
    caseInsensitiveOption,
    unknownValue: value !== '' && !exactOption && !caseInsensitiveOption
  };
}

/** Dropdown content for a stored value no option carries verbatim. */
function unmatchedValueContent(
  caseInsensitiveOption: DraftSelectOption | undefined,
  unknownValueContent: ReactNode | undefined,
  value: string
): ReactNode {
  if (caseInsensitiveOption) return caseInsensitiveOption.display ?? caseInsensitiveOption.label;
  return unknownValueContent ?? <span className="text-danger">{value}</span>;
}

/**
 * The one draft table of the setup wizard: every step renders its parsed
 * Excel rows (and manually added ones) through this, with entity-reference
 * columns as dropdowns and everything editable in place. Edits re-validate
 * the row with the same schema the Excel parser used.
 */
export function EditableDraftTable<TData, TRow extends DraftRowShape<TData>>({
  rows,
  columns,
  readOnly = false,
  onRowsChange,
  validateRow,
  duplicateChecks,
  makeEmptyRow,
  addLabelKey,
  titleKey,
  toolbar,
  deleteAction,
  deleteDisabledTitle,
  rowClassName
}: EditableDraftTableProps<TData, TRow>) {
  const { t } = useTranslation();

  const commitRows = (next: TRow[]) => {
    if (onRowsChange && duplicateChecks) {
      onRowsChange(clearStaleDuplicateMarks(next, duplicateChecks));
    }
  };

  const updateCell = (index: number, field: keyof TData & string, value: unknown) => {
    if (!onRowsChange || !validateRow) return;
    const column = columns.find((candidate) => candidate.field === field);
    const previousValue = rows[index]?.data[field];
    const edited = rows.map((row, i) => (i === index ? { ...row.data, [field]: value } : row.data));
    const synced = column?.syncRowsAfterEdit
      ? column.syncRowsAfterEdit(
          edited,
          index,
          typeof previousValue === 'string' ? previousValue : undefined
        )
      : edited;
    commitRows(
      rows.map((row, i) => {
        const data = synced[i];
        if (data === row.data) return row;
        // Rows already showing errors re-validate live so fixes clear
        // immediately (including siblings a sync rewrote); everything else
        // stays neutral until Weiter runs the authoritative validateDraftRows
        // pass.
        if (row.isValid) return { ...row, data };
        const { isValid, errors } = validateRow(data);
        return { ...row, data, isValid, errors };
      })
    );
  };

  const removeRow = (index: number) => {
    commitRows(rows.filter((_, i) => i !== index));
  };

  const addRow = () => {
    if (!onRowsChange || !makeEmptyRow) return;
    const nextRowNumber = rows.reduce((max, row) => Math.max(max, row.rowNumber), 1) + 1;
    onRowsChange([...rows, makeEmptyRow(nextRowNumber)]);
  };

  // Errors whose `field` matches no column would otherwise be invisible
  // with per-cell hints only, so they keep a row-level list.
  const orphanErrors = (row: TRow): FieldError[] => {
    if (row.isValid || !row.errors) return [];
    return row.errors.filter((error) => !columns.some((column) => error.field === column.field));
  };

  // A column-locked cell in an otherwise editable table — e.g. the identity
  // columns of a protected member row.
  const lockedCell = (row: TRow, column: DraftColumnDef<TData>) => {
    const options = typeof column.options === 'function' ? column.options(row) : column.options;
    return (
      <div
        className="flex h-[34px] items-center px-2.5"
        title={column.readOnlyHintKey ? t(column.readOnlyHintKey) : undefined}
      >
        {readOnlyCellContent(row.data[column.field], options)}
      </div>
    );
  };

  const renderCell = (row: TRow, index: number, column: DraftColumnDef<TData>) => {
    const rawValue = row.data[column.field];
    const options = typeof column.options === 'function' ? column.options(row) : column.options;

    if (readOnly) return readOnlyCellContent(rawValue, options);

    const errorMessage = !row.isValid ? fieldFirstError(row.errors, column.field) : undefined;
    const errorClass = errorMessage !== undefined && 'border-danger-solid bg-danger-solid/5';

    const errorHint =
      errorMessage !== undefined && errorMessage !== '' ? (
        <div className="mt-1 text-[10.5px] text-danger">{errorMessage}</div>
      ) : null;

    if (options) {
      const value = typeof rawValue === 'string' ? rawValue : '';
      // An Excel value that matches no known entity must stay visible so the
      // user understands what the cell error refers to.
      const { exactOption, caseInsensitiveOption, unknownValue } = matchSelectValue(value, options);
      return (
        <div>
          <Select
            value={value === '' ? NONE_VALUE : value}
            onValueChange={(selected) =>
              updateCell(index, column.field, selected === NONE_VALUE ? undefined : selected)
            }
          >
            <SelectTrigger
              className={cn(
                CELL_CONTROL_CLASS,
                'w-full min-w-32',
                column.cellClassName,
                errorClass,
                unknownValue &&
                  column.unknownValueContent &&
                  'border-danger-solid bg-danger-solid/5'
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {column.optional && (
                <SelectItem value={NONE_VALUE} indicatorSide="right">
                  <span className="text-fg-subtle">{t('initialSetup.draftTable.noSelection')}</span>
                </SelectItem>
              )}
              {value !== '' && !exactOption && (
                <SelectItem value={value} indicatorSide="right">
                  {unmatchedValueContent(caseInsensitiveOption, column.unknownValueContent, value)}
                </SelectItem>
              )}
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value} indicatorSide="right">
                  {option.display ?? option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errorHint}
        </div>
      );
    }

    const textValue =
      rawValue === undefined || rawValue === null ? '' : String(rawValue as string | number);
    return (
      <div>
        <Input
          value={textValue}
          onChange={(event) => {
            const next = event.target.value;
            if (column.inputType === 'number') {
              updateCell(index, column.field, next === '' ? undefined : Number(next));
            } else {
              // Optional fields must clear to undefined — '' fails z.email()
              // etc.; required strings keep '' so min(1) yields its message.
              updateCell(index, column.field, next === '' && column.optional ? undefined : next);
            }
          }}
          className={cn(CELL_CONTROL_CLASS, column.cellClassName, errorClass)}
        />
        {errorHint}
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-white shadow-xs">
      {(titleKey || toolbar) && (
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div className="flex items-center gap-2">
            {titleKey && <span className="text-sm font-bold text-foreground">{t(titleKey)}</span>}
            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-2xs font-semibold text-fg-secondary">
              {rows.length}
            </span>
          </div>
          {toolbar && <div className="flex items-center gap-2">{toolbar}</div>}
        </div>
      )}
      <Table className="text-sm">
        <TableHeader className="border-b border-line bg-surface-subtle text-left [&_tr]:border-b-0">
          <TableRow className="border-b-0 hover:bg-transparent">
            <TableHead className="h-auto w-[34px] bg-transparent py-2.75 pl-4 pr-2 align-top text-[11.5px] font-semibold text-muted-foreground">
              #
            </TableHead>
            {columns.map((column) => (
              <TableHead
                key={column.field}
                className={cn(
                  'h-auto bg-transparent px-2 py-2.75 align-top text-[11.5px] font-semibold text-muted-foreground',
                  column.widthClass
                )}
              >
                {stripRequiredMarker(t(column.headerKey))}
                {column.required && ' *'}
                {column.descriptionKey && (
                  <div className="mt-0.5 text-3xs font-normal leading-[1.3] text-fg-subtle">
                    {t(column.descriptionKey)}
                  </div>
                )}
              </TableHead>
            ))}
            {!readOnly && (
              <TableHead className="h-auto w-[34px] bg-transparent py-2.75 pl-2 pr-4 align-top" />
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => {
            const rowOrphanErrors = orphanErrors(row);
            const rowDeleteAction = deleteAction ? deleteAction(row) : 'enabled';
            return (
              <Fragment key={`row-${row.rowNumber}`}>
                <TableRow
                  className={cn(
                    'border-t border-b-0 border-line-subtle first:border-t-0 hover:bg-transparent',
                    rowClassName?.(row)
                  )}
                >
                  <TableCell className="pb-2.25 pt-4.25 pl-4 pr-2 align-top font-mono text-xs text-fg-subtle">
                    {index + 1}
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell
                      key={column.field}
                      className={cn('align-top', readOnly ? 'px-2 py-2.5' : 'px-2 py-2.25')}
                    >
                      {!readOnly && resolveColumnReadOnly(column, row)
                        ? lockedCell(row, column)
                        : renderCell(row, index, column)}
                    </TableCell>
                  ))}
                  {!readOnly && (
                    <TableCell className="py-2.25 pl-2 pr-4 align-top">
                      {rowDeleteAction !== 'hidden' && (
                        <button
                          type="button"
                          onClick={() => rowDeleteAction === 'enabled' && removeRow(index)}
                          disabled={rowDeleteAction === 'disabled'}
                          aria-label={
                            rowDeleteAction === 'enabled'
                              ? t('initialSetup.draftTable.removeRow')
                              : deleteDisabledTitle
                          }
                          title={rowDeleteAction === 'disabled' ? deleteDisabledTitle : undefined}
                          className={cn(
                            'flex h-[34px] items-center justify-center transition-colors',
                            rowDeleteAction === 'enabled'
                              ? 'text-fg-subtle hover:text-fg-secondary'
                              : 'cursor-not-allowed text-fg-disabled'
                          )}
                        >
                          <Trash2 className="h-[15px] w-[15px]" />
                        </button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
                {rowOrphanErrors.length > 0 && (
                  <TableRow className="border-b-0 hover:bg-transparent">
                    <TableCell className="p-0" />
                    <TableCell colSpan={columns.length + 1} className="px-2 pb-2 pt-0">
                      <ul className="space-y-0.5 text-[10.5px] text-danger">
                        {rowOrphanErrors.map((error, errorIndex) => (
                          <li key={errorIndex}>• {error.message}</li>
                        ))}
                      </ul>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
          {!readOnly && addLabelKey && (
            <TableRow className="border-b-0 hover:bg-transparent">
              <TableCell
                colSpan={columns.length + 2}
                className="border-t border-dashed border-line-strong p-0"
              >
                <button
                  type="button"
                  onClick={addRow}
                  className="flex w-full items-center gap-2 px-4 py-2.75 text-[13px] font-semibold text-bliks-green-700 transition-colors hover:bg-surface-subtle"
                >
                  <Plus className="h-[15px] w-[15px]" />
                  {t(addLabelKey)}
                </button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
